import {
    afterRenderEffect,
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    linkedSignal,
    signal,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import { EmbeddedContent } from "./s25.embedded.content.util";
import { s25Resource } from "../../util/s25.resource";
import { S25Util } from "../../util/s25-util";
import { S25EmbeddingUtil } from "../../modules/system-settings/embedding/s25-embedding/s25.embedding.util";
import { S25LoadingInlineStaticComponent } from "../../modules/s25-loading/s25.loading.inline.static.component";

/** Hide the spinner this long after the frame loads if the 25Live app never posts a ready message. */
const FRAME_READY_FALLBACK_MS = 5000;
const IDLE_TIMEOUT_MS = 2000;

/** Runs the callback once the browser is idle so the embedded app doesn't compete with the page's own startup. */
function whenIdle(callback: () => void): () => void {
    if ("requestIdleCallback" in window) {
        const idleHandle = window.requestIdleCallback(callback, { timeout: IDLE_TIMEOUT_MS });
        return () => window.cancelIdleCallback(idleHandle);
    }
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    const frameHandle = requestAnimationFrame(() => {
        timeoutHandle = setTimeout(callback, 0);
    });
    return () => {
        cancelAnimationFrame(frameHandle);
        clearTimeout(timeoutHandle);
    };
}

export type EmbeddedContentMode = "video" | "panel";

@Component({
    standalone: true,
    selector: "s25-ng-embedded-content",
    imports: [S25LoadingInlineStaticComponent],
    template: `
        @if (!hasTitle()) {
            @if (showMissingTitleWarning()) {
                <p class="embed-warning">Add an accessible title to show this embedded content.</p>
            }
        } @else if (iframeTarget() && safeUrl()) {
            <div
                class="embed-container"
                [class.video-mode]="isVideoMode()"
                [class.portrait]="iframeTarget()!.shape === 'portrait'"
                [class.post]="iframeTarget()!.shape === 'post'"
            >
                <iframe
                    [src]="safeUrl()"
                    [title]="accessibleTitle()"
                    sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
                    allow="fullscreen"
                    loading="lazy"
                    class="embed-frame"
                ></iframe>
            </div>
        } @else if (embedded25Target() && embedded25LoadFailed()) {
            <p class="embed-warning">This embedded content couldn't be loaded. Try refreshing the page.</p>
        } @else if (embedded25Target()) {
            <div
                #embedded25Container
                class="embed-container embedded25"
                [class.loading]="!embedded25FrameReady()"
                role="region"
                [attr.aria-label]="accessibleTitle()"
                [attr.aria-busy]="!embedded25FrameReady()"
            >
                @if (!embedded25FrameReady()) {
                    <s25-ng-loading-inline-static />
                }
                <div #embedded25Host [attr.id]="embedded25Target()?.targetId"></div>
            </div>
        } @else if (showUnsupportedWarning()) {
            <p class="embed-warning">This embed code isn't supported. Use the provider's link or iframe embed code.</p>
        }
    `,
    styles: `
        :host {
            display: block;
        }

        .embed-container {
            width: 100%;
            border-radius: 8px;
            overflow: hidden;
            background: var(--color--surface-bg);
        }

        .embed-container.embedded25.loading {
            min-height: 120px;
            padding: 1rem;
        }

        .embed-container.video-mode {
            aspect-ratio: 16 / 9;
        }

        .embed-container.video-mode.portrait {
            aspect-ratio: 9 / 16;
            max-width: 340px;
            margin-inline: auto;
        }

        .embed-container.post {
            aspect-ratio: auto;
            max-width: 550px;
            margin-inline: auto;
        }

        .embed-frame {
            width: 100%;
            height: 100%;
            border: none;
            display: block;
        }

        .embed-container:not(.video-mode) .embed-frame {
            min-height: 400px;
            height: 600px;
        }

        .embed-container.post .embed-frame {
            height: 640px;
        }

        .embed-warning {
            color: var(--error-message-color);
            font-style: italic;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
})
export class S25EmbeddedContentComponent {
    /** A URL or pasted embed code. */
    readonly source = input<string>("");
    readonly accessibleTitle = input<string>("");
    readonly mode = input<EmbeddedContentMode>("video");
    /** Show setup problems (missing title, unsupported embed code) to users who can fix them. */
    readonly showConfigWarnings = input(false);

    private readonly sanitizer = inject(DomSanitizer);

    private readonly embedded25Host = viewChild<ElementRef<HTMLElement>>("embedded25Host");

    protected readonly hasTitle = computed(() => !!this.accessibleTitle()?.trim());
    protected readonly isVideoMode = computed(() => this.mode() !== "panel");
    private readonly hasUrl = computed(() => !!this.source()?.trim());

    protected readonly embed = computed(() => EmbeddedContent.parse(this.source() ?? ""));
    protected readonly iframeTarget = computed(() => {
        const embed = this.embed();
        return embed?.kind === "iframe" ? embed : undefined;
    });
    protected readonly safeUrl = computed(() => {
        const iframe = this.iframeTarget();
        return iframe ? this.sanitizer.bypassSecurityTrustResourceUrl(iframe.url) : undefined;
    });
    protected readonly embedded25Target = computed(() => {
        const embed = this.embed();
        return embed?.kind === "embedded25" ? embed : undefined;
    });

    private readonly embedded25Container = viewChild<ElementRef<HTMLElement>>("embedded25Container");
    /** Set by an IntersectionObserver; the 25Live app is not loaded until it scrolls into view. */
    private readonly embedded25Visible = signal(false);
    /** Set by the frame's own messages or load event, and reset whenever the pasted embed changes. */
    protected readonly embedded25FrameReady = linkedSignal({ source: this.embedded25Target, computation: () => false });

    private readonly embedded25Script = s25Resource({
        if: () => !!this.embedded25Target() && this.hasTitle() && this.embedded25Visible(),
        loader: () => S25Util.loadScript("embedded25", S25EmbeddingUtil.clientScriptUrl()),
    });
    protected readonly embedded25LoadFailed = computed(
        () => this.embedded25Script.value() === false || !!this.embedded25Script.error(),
    );

    protected readonly showMissingTitleWarning = computed(
        () => !this.hasTitle() && this.hasUrl() && this.showConfigWarnings(),
    );
    protected readonly showUnsupportedWarning = computed(
        () => !this.embed() && this.hasUrl() && this.showConfigWarnings(),
    );

    constructor() {
        afterRenderEffect((onCleanup) => {
            const container = this.embedded25Container()?.nativeElement;
            if (!container || this.embedded25Visible()) return;
            const observer = new IntersectionObserver((entries) => {
                if (entries.some((entry) => entry.isIntersecting)) this.embedded25Visible.set(true);
            });
            observer.observe(container);
            onCleanup(() => observer.disconnect());
        });

        afterRenderEffect((onCleanup) => {
            const target = this.embedded25Target();
            const host = this.embedded25Host()?.nativeElement;
            if (!target || !host || !this.embedded25Script.value()) return;

            const isFromFrame = (source: MessageEventSource | null) =>
                !!source && source === host.querySelector("iframe")?.contentWindow;
            const onFrameMessage = (event: MessageEvent) => {
                if (isFromFrame(event.source)) this.embedded25FrameReady.set(true);
            };
            let loadFallback: ReturnType<typeof setTimeout> | undefined;
            const onFrameLoad = () => {
                clearTimeout(loadFallback);
                loadFallback = setTimeout(() => this.embedded25FrameReady.set(true), FRAME_READY_FALLBACK_MS);
            };
            window.addEventListener("message", onFrameMessage);
            host.addEventListener("load", onFrameLoad, true);

            const cancelIdle = whenIdle(() => {
                host.replaceChildren();
                window.embedded25?.fromConfig.create(target.instanceId, target.configToken);
            });

            onCleanup(() => {
                cancelIdle();
                clearTimeout(loadFallback);
                window.removeEventListener("message", onFrameMessage);
                host.removeEventListener("load", onFrameLoad, true);
                host.replaceChildren();
                window.embedded25?.clearEventListeners();
            });
        });
    }
}
